"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.nullToUndefined = nullToUndefined;
exports.emptyToNull = emptyToNull;
exports.employeeModelToAPISchema = employeeModelToAPISchema;
exports.companyModelToAPISchema = companyModelToAPISchema;
exports.companySummaryToAPISchema = companySummaryToAPISchema;
exports.venueModelToAPISchema = venueModelToAPISchema;
exports.departmentModelToAPISchema = departmentModelToAPISchema;
exports.positionModelToAPISchema = positionModelToAPISchema;
exports.positionAPISchemaToModel = positionAPISchemaToModel;
exports.timeEntryModelToAPISchema = timeEntryModelToAPISchema;
exports.timeEntryAPISchemaToModel = timeEntryAPISchemaToModel;
exports.timeEntryPublishedInfoModelToAPISchema = timeEntryPublishedInfoModelToAPISchema;
exports.timeEntryPublishedInfoAPISchemaToModel = timeEntryPublishedInfoAPISchemaToModel;
exports.timeLogEventModelToAPISchema = timeLogEventModelToAPISchema;
exports.documentToAPISchema = documentToAPISchema;
const core_1 = require("@pentacode/core");
// turns null values to undefined to represent an empty value in typeorm and the database
function nullToUndefined(obj) {
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = obj[key] === null ? undefined : obj[key];
        return acc;
    }, {}); // eslint-disable-line @typescript-eslint/no-explicit-any
}
// turns empty values ['undefined' and empty string ''] to null to be returned as JSON in API
function emptyToNull(obj) {
    return Object.keys(obj).reduce((acc, key) => {
        acc[key] = obj[key] === undefined || obj[key] === "" ? null : obj[key];
        return acc;
    }, {}); // eslint-disable-line @typescript-eslint/no-explicit-any
}
function employeeModelToAPISchema(company, employee, includeRestricted) {
    return emptyToNull(includeRestricted
        ? {
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
            title: employee.title,
            callName: employee.callName,
            address: employee.address,
            city: employee.city,
            postalCode: employee.postalCode,
            email: employee.email,
            phone: employee.phone,
            phone2: employee.phone2,
            birthday: employee.birthday,
            birthName: employee.birthName,
            birthCity: employee.birthCity,
            birthCountry: employee.birthCountry,
            nationality: employee.nationality,
            gender: employee.gender,
            taxId: employee.taxId,
            socialSecurityNumber: employee.socialSecurityNumber,
            notes: employee.notes,
            staffNumber: employee.staffNumber?.toString(),
            avatar: employee.avatar,
            positions: employee.positions?.map((position) => {
                const color = company.getPositionColor(position);
                return {
                    id: position.id,
                    name: position.name,
                    color: (color && (0, core_1.getHexColor)(color)) || color,
                };
            }) || [],
            timeLogPin: employee.timePin,
            status: employee.status,
        }
        : {
            id: employee.id,
            firstName: employee.firstName,
            lastName: employee.lastName,
            title: employee.title,
            callName: employee.callName,
            staffNumber: employee.staffNumber?.toString(),
            avatar: employee.avatar,
            positions: employee.positions?.map((position) => {
                const color = company.getPositionColor(position);
                return {
                    id: position.id,
                    name: position.name,
                    color: (color && (0, core_1.getHexColor)(color)) || color,
                };
            }) || [],
            timeLogPin: employee.timePin,
            status: employee.status,
        });
}
function companyModelToAPISchema(company) {
    return emptyToNull({
        id: company.id,
        name: company.name,
        venues: company.venues.map(venueModelToAPISchema),
    });
}
function companySummaryToAPISchema(company) {
    return { id: company.id, name: company.name };
}
function venueModelToAPISchema(venue) {
    const { id, name, venueNumber, state, holidays, enabledHolidays, departments } = venue;
    return emptyToNull({
        id,
        name,
        companyId: venue.companyId,
        venueNumber,
        state,
        holidays: holidays || enabledHolidays,
        departments: departments?.map(departmentModelToAPISchema) || [],
    });
}
function departmentModelToAPISchema(department) {
    return emptyToNull({
        id: department.id,
        name: department.name,
        venueId: department.venueId,
        color: (0, core_1.getHexColor)(department.color),
        positions: department.positions?.map(positionModelToAPISchema),
        timeScheduleEmployeeOrder: department.rosterOrder?.map((id) => Number(id)),
    });
}
function positionModelToAPISchema(position) {
    return emptyToNull({
        id: position.id,
        name: position.name,
        color: (0, core_1.getHexColor)(position.color),
        departmentId: position.departmentId,
        ...(position.distinctName !== undefined && { distinctName: position.distinctName }),
    });
}
function positionAPISchemaToModel(position) {
    return new core_1.Position(nullToUndefined({
        id: position.id,
        name: position.name,
        color: position.color,
    }));
}
function timeEntryModelToAPISchema(company, entry) {
    const position = company.getPosition(entry.positionId)?.position;
    const type = entry.type === core_1.TimeEntryType.Work ? "work" : entry.type;
    return {
        id: entry.id,
        date: entry.date,
        status: getStatus(entry),
        startPlanned: entry.startPlanned?.toISOString() || null,
        endPlanned: entry.endPlanned?.toISOString() || null,
        startLogged: entry.startLogged?.toISOString() || null,
        endLogged: entry.endLogged?.toISOString() || null,
        startFinal: entry.startFinal?.toISOString() || null,
        endFinal: entry.endFinal?.toISOString() || null,
        currentBreakStart: entry.startBreak?.toISOString() || null,
        employeeId: entry.employeeId || null,
        type,
        breakPlanned: typeof entry.breakPlanned === "number" ? entry.breakPlanned : 0,
        breakLogged: typeof entry.breakLogged === "number" ? entry.breakLogged : 0,
        breakFinal: typeof entry.break === "number" ? entry.break : 0,
        result: entry.result || undefined,
        position: (position &&
            positionModelToAPISchema({
                ...position,
                color: company.getPositionColor(position),
            })) ||
            null,
        mealsBreakfast: entry.mealsBreakfast,
        published: timeEntryPublishedInfoModelToAPISchema(company, entry.published),
        seen: timeEntryPublishedInfoModelToAPISchema(company, entry.seen),
        mealsLunch: entry.mealsLunch,
        mealsDinner: entry.mealsDinner,
        comment: entry.comment,
    };
}
function timeEntryAPISchemaToModel(entry) {
    const timeEntryBase = nullToUndefined({
        id: entry.id,
        date: entry.date,
        startPlanned: entry.startPlanned ? new Date(entry.startPlanned) : null,
        endPlanned: entry.endPlanned ? new Date(entry.endPlanned) : null,
        startLogged: entry.startLogged ? new Date(entry.startLogged) : null,
        endLogged: entry.endLogged ? new Date(entry.endLogged) : null,
        startFinal: entry.startFinal ? new Date(entry.startFinal) : null,
        endFinal: entry.endFinal ? new Date(entry.endFinal) : null,
        startBreak: entry.currentBreakStart ? new Date(entry.currentBreakStart) : null,
        employeeId: entry.employeeId || null,
        type: entry.type,
        breakPlanned: entry.breakPlanned,
        breakLogged: entry.breakLogged,
        break: entry.breakFinal,
        result: entry.result,
        positionId: entry.position?.id || null,
        mealsBreakfast: entry.mealsBreakfast,
        published: entry.published ? timeEntryPublishedInfoAPISchemaToModel(entry.published) : null,
        seen: entry.seen ? timeEntryPublishedInfoAPISchemaToModel(entry.seen) : null,
        mealsLunch: entry.mealsLunch,
        mealsDinner: entry.mealsDinner,
        comment: entry.comment,
        position: entry.position ? positionAPISchemaToModel(entry.position) : null,
    });
    return new core_1.TimeEntry(timeEntryBase);
}
function timeEntryPublishedInfoModelToAPISchema(company, info) {
    if (!info) {
        return info;
    }
    const position = info.positionId && company.getPosition(info.positionId)?.position;
    return {
        ...info,
        position: position ? positionModelToAPISchema(position) : null,
        time: info.time.toISOString(),
        deleted: info.deleted?.toISOString() || null,
        startPlanned: info.startPlanned?.toISOString() || null,
        endPlanned: info.endPlanned?.toISOString() || null,
    };
}
function timeEntryPublishedInfoAPISchemaToModel(info) {
    return {
        time: new Date(info.time),
        deleted: info.deleted ? new Date(info.deleted) : null,
        employeeId: info.employeeId,
        date: info.date,
        positionId: info.position?.id || null,
        startPlanned: info.startPlanned ? new Date(info.startPlanned) : null,
        endPlanned: info.endPlanned ? new Date(info.endPlanned) : null,
        comment: info.comment,
    };
}
function timeLogEventModelToAPISchema({ id, action, deviceId, employeeId, positionId, timeEntryId, time, status, rejectedReason, location, image, }) {
    return {
        id,
        action,
        deviceId,
        employeeId,
        positionId,
        timeEntryId,
        time: time.toISOString(),
        status,
        rejectedReason,
        location,
        image: image ?? undefined,
    };
}
function documentToAPISchema(document) {
    return {
        id: document.id,
        uuid: document.uuid,
        companyId: document.companyId,
        employeeId: document.employeeId,
        createdById: document.createdById,
        name: document.name,
        comment: document.comment,
        date: document.date,
        type: document.type,
        tags: document.tags.map((tag) => tag.name),
        url: document.url,
    };
}
function getStatus(entry) {
    if (entry.type !== core_1.TimeEntryType.Work) {
        return "completed";
    }
    if (entry.endFinal) {
        return "completed";
    }
    if (entry.startBreak) {
        return "paused";
    }
    if (entry.startFinal) {
        return "ongoing";
    }
    return "scheduled";
}
//# sourceMappingURL=transform.js.map